/**
 * Aggregate analytics across multiple consulta analyses.
 *
 * Pure function — accepts ConsultaAI[] and returns aggregated patterns:
 * personality distribution, common flags, semantic field, stage averages,
 * temporal seasonality.
 *
 * Source payload shape: ConsultaReport (v4) inside `analysisPayload`.
 */
import type { ConsultaAI } from "../types";
import type {
  ConsultaReport,
  EtapaSlug,
  AnalysisGrade,
  FlagCode,
  TermoSemantico,
} from "@/lib/analysis/types";

export interface PersonalidadeBucket {
  codigo: string;
  nome: string;
  count: number;
  pct: number;
  centroIntel?: string;
}

export interface FlagBucket {
  flag: FlagCode;
  count: number;
  impactoAlto: number;
  impactoMedio: number;
  impactoBaixo: number;
  exemplos: string[];
}

export interface EtapaBucket {
  etapa: EtapaSlug;
  count: number;
  pct: number;
}

export interface GargaloBucket {
  etapa: EtapaSlug;
  count: number;
  exemplos: string[];
}

export interface TermoAggregate {
  termo: string;
  totalFreq: number;
  consultas: number;
  cargaPredominante: "alta" | "media" | "baixa";
  exemploFrase?: string;
}

export interface CampoSemanticoAggregate {
  dor: TermoAggregate[];
  desejo: TermoAggregate[];
  objecao: TermoAggregate[];
  expressoesMarcantes: { texto: string; count: number }[];
  palavrasChaveCentrais: { termo: string; count: number }[];
}

export interface MediasScore {
  methodTotalAvg: number | null;
  scoreFinalAvg: number | null;
  porEtapa: Partial<Record<EtapaSlug, number>>;
  gradeDistribution: Partial<Record<AnalysisGrade, number>>;
}

export interface SazonalidadeBucket {
  porMes: { mes: string; count: number }[];
  porDiaSemana: { dia: string; count: number }[];
}

export interface ConsultasAggregate {
  total: number;
  comAnaliseValida: number;
  intervaloDatas: { inicio: string | null; fim: string | null };
  personalidades: PersonalidadeBucket[];
  flags: FlagBucket[];
  etapaCriticaFreq: EtapaBucket[];
  gargalos: GargaloBucket[];
  campoSemantico: CampoSemanticoAggregate;
  medias: MediasScore;
  sazonalidade: SazonalidadeBucket;
}

const DIAS_SEMANA = ["dom", "seg", "ter", "qua", "qui", "sex", "sab"];
const MESES = [
  "jan", "fev", "mar", "abr", "mai", "jun",
  "jul", "ago", "set", "out", "nov", "dez",
];

function parsePayload(raw: unknown): ConsultaReport | null {
  if (!raw) return null;
  let candidate: unknown = raw;
  if (typeof raw === "string") {
    const cleaned = raw
      .replace(/^```(?:json)?\s*/i, "")
      .replace(/```\s*$/i, "")
      .trim();
    try {
      candidate = JSON.parse(cleaned);
    } catch {
      return null;
    }
  }
  if (!candidate || typeof candidate !== "object") return null;
  const o = candidate as Record<string, unknown>;
  if (
    "method_total" in o &&
    "method_stages" in o &&
    o.method_stages &&
    typeof (o.method_stages as Record<string, unknown>).conexao === "object"
  ) {
    return o as unknown as ConsultaReport;
  }
  return null;
}

function normalizeTermo(t: string): string {
  return t.trim().toLowerCase();
}

function aggregateTermos(lists: TermoSemantico[][]): TermoAggregate[] {
  const map = new Map<
    string,
    { totalFreq: number; consultas: number; cargas: Record<string, number>; exemplo?: string; display: string }
  >();
  for (const list of lists) {
    const seenInList = new Set<string>();
    for (const t of list) {
      if (!t?.termo) continue;
      const key = normalizeTermo(t.termo);
      if (!key) continue;
      const entry = map.get(key) ?? {
        totalFreq: 0,
        consultas: 0,
        cargas: { alta: 0, media: 0, baixa: 0 },
        exemplo: t.exemplo_frase,
        display: t.termo.trim(),
      };
      entry.totalFreq += t.frequencia ?? 1;
      if (!seenInList.has(key)) {
        entry.consultas += 1;
        seenInList.add(key);
      }
      if (t.carga_emocional && t.carga_emocional in entry.cargas) {
        entry.cargas[t.carga_emocional] += 1;
      }
      if (!entry.exemplo && t.exemplo_frase) entry.exemplo = t.exemplo_frase;
      map.set(key, entry);
    }
  }
  return Array.from(map.entries())
    .map(([_key, v]) => {
      const cargaPredominante = (
        ["alta", "media", "baixa"] as const
      ).reduce((acc, c) => (v.cargas[c] > v.cargas[acc] ? c : acc), "media" as const);
      return {
        termo: v.display,
        totalFreq: v.totalFreq,
        consultas: v.consultas,
        cargaPredominante,
        exemploFrase: v.exemplo,
      };
    })
    .sort((a, b) => b.totalFreq - a.totalFreq);
}

function aggregateStringCounts(lists: string[][]): { texto: string; count: number }[] {
  const map = new Map<string, { count: number; display: string }>();
  for (const list of lists) {
    for (const raw of list) {
      if (!raw) continue;
      const key = normalizeTermo(raw);
      if (!key) continue;
      const entry = map.get(key) ?? { count: 0, display: raw.trim() };
      entry.count += 1;
      map.set(key, entry);
    }
  }
  return Array.from(map.values())
    .map((v) => ({ texto: v.display, count: v.count }))
    .sort((a, b) => b.count - a.count);
}

export function aggregateConsultas(consultas: ConsultaAI[]): ConsultasAggregate {
  const reports: { report: ConsultaReport; createdAt: string }[] = [];
  for (const c of consultas) {
    const r = parsePayload(c.analysisPayload);
    if (r) reports.push({ report: r, createdAt: c.createdAt });
  }

  const total = consultas.length;
  const comAnaliseValida = reports.length;

  // ── intervalo datas ────────────────────────────────────────────────────────
  const datas = consultas.map((c) => c.createdAt).filter(Boolean).sort();
  const intervaloDatas = {
    inicio: datas[0] ?? null,
    fim: datas[datas.length - 1] ?? null,
  };

  // ── personalidades ─────────────────────────────────────────────────────────
  const personalidadesMap = new Map<
    string,
    { codigo: string; nome: string; centroIntel?: string; count: number }
  >();
  for (const { report } of reports) {
    const p = report.personalidade_paciente;
    if (!p?.codigo) continue;
    const entry = personalidadesMap.get(p.codigo) ?? {
      codigo: p.codigo,
      nome: p.nome ?? p.codigo,
      centroIntel: p.centro_inteligencia,
      count: 0,
    };
    entry.count += 1;
    personalidadesMap.set(p.codigo, entry);
  }
  const totalPers = Array.from(personalidadesMap.values()).reduce(
    (s, v) => s + v.count,
    0,
  );
  const personalidades: PersonalidadeBucket[] = Array.from(personalidadesMap.values())
    .map((v) => ({
      codigo: v.codigo,
      nome: v.nome,
      centroIntel: v.centroIntel,
      count: v.count,
      pct: totalPers > 0 ? (v.count / totalPers) * 100 : 0,
    }))
    .sort((a, b) => b.count - a.count);

  // ── flags ──────────────────────────────────────────────────────────────────
  const flagsMap = new Map<FlagCode, FlagBucket>();
  for (const { report } of reports) {
    for (const f of report.flags_criticas ?? []) {
      if (!f?.detectada) continue;
      const entry = flagsMap.get(f.flag) ?? {
        flag: f.flag,
        count: 0,
        impactoAlto: 0,
        impactoMedio: 0,
        impactoBaixo: 0,
        exemplos: [],
      };
      entry.count += 1;
      if (f.impacto === "alto") entry.impactoAlto += 1;
      else if (f.impacto === "medio") entry.impactoMedio += 1;
      else if (f.impacto === "baixo") entry.impactoBaixo += 1;
      if (f.evidencia && entry.exemplos.length < 3) entry.exemplos.push(f.evidencia);
      flagsMap.set(f.flag, entry);
    }
  }
  const flags = Array.from(flagsMap.values()).sort((a, b) => b.count - a.count);

  // ── etapa crítica ──────────────────────────────────────────────────────────
  const etapaMap = new Map<EtapaSlug, number>();
  for (const { report } of reports) {
    if (report.etapa_critica) {
      etapaMap.set(report.etapa_critica, (etapaMap.get(report.etapa_critica) ?? 0) + 1);
    }
  }
  const totalEtapas = Array.from(etapaMap.values()).reduce((s, v) => s + v, 0);
  const etapaCriticaFreq: EtapaBucket[] = Array.from(etapaMap.entries())
    .map(([etapa, count]) => ({
      etapa,
      count,
      pct: totalEtapas > 0 ? (count / totalEtapas) * 100 : 0,
    }))
    .sort((a, b) => b.count - a.count);

  // ── gargalos ───────────────────────────────────────────────────────────────
  const gargaloMap = new Map<EtapaSlug, GargaloBucket>();
  for (const { report } of reports) {
    const g = report.gargalo_principal;
    if (!g?.etapa) continue;
    const entry = gargaloMap.get(g.etapa) ?? { etapa: g.etapa, count: 0, exemplos: [] };
    entry.count += 1;
    if (g.descricao && entry.exemplos.length < 3) entry.exemplos.push(g.descricao);
    gargaloMap.set(g.etapa, entry);
  }
  const gargalos = Array.from(gargaloMap.values()).sort((a, b) => b.count - a.count);

  // ── campo semântico ────────────────────────────────────────────────────────
  const dorLists: TermoSemantico[][] = [];
  const desejoLists: TermoSemantico[][] = [];
  const objecaoLists: TermoSemantico[][] = [];
  const expressoesLists: string[][] = [];
  const palavrasChaveLists: string[][] = [];
  for (const { report } of reports) {
    const cs = report.campo_semantico;
    if (!cs) continue;
    if (cs.dor) dorLists.push(cs.dor);
    if (cs.desejo) desejoLists.push(cs.desejo);
    if (cs.objecao) objecaoLists.push(cs.objecao);
    if (cs.expressoes_marcantes) expressoesLists.push(cs.expressoes_marcantes);
    if (cs.palavra_chave_central) palavrasChaveLists.push([cs.palavra_chave_central]);
  }
  const campoSemantico: CampoSemanticoAggregate = {
    dor: aggregateTermos(dorLists),
    desejo: aggregateTermos(desejoLists),
    objecao: aggregateTermos(objecaoLists),
    expressoesMarcantes: aggregateStringCounts(expressoesLists),
    palavrasChaveCentrais: aggregateStringCounts(palavrasChaveLists).map((e) => ({
      termo: e.texto,
      count: e.count,
    })),
  };

  // ── médias ─────────────────────────────────────────────────────────────────
  let methodSum = 0;
  let methodCount = 0;
  let finalSum = 0;
  let finalCount = 0;
  const stageSums: Partial<Record<EtapaSlug, { sum: number; n: number }>> = {};
  const gradeDistribution: Partial<Record<AnalysisGrade, number>> = {};
  for (const { report } of reports) {
    if (typeof report.method_total === "number") {
      methodSum += report.method_total;
      methodCount += 1;
    }
    if (typeof report.score_final === "number") {
      finalSum += report.score_final;
      finalCount += 1;
    }
    if (report.grade) {
      gradeDistribution[report.grade] = (gradeDistribution[report.grade] ?? 0) + 1;
    }
    const stages = report.method_stages;
    if (stages) {
      for (const slug of Object.keys(stages) as EtapaSlug[]) {
        const ev = stages[slug as keyof typeof stages];
        if (ev && typeof ev.score === "number") {
          const acc = stageSums[slug] ?? { sum: 0, n: 0 };
          acc.sum += ev.score;
          acc.n += 1;
          stageSums[slug] = acc;
        }
      }
    }
  }
  const porEtapa: Partial<Record<EtapaSlug, number>> = {};
  for (const [slug, v] of Object.entries(stageSums)) {
    if (v && v.n > 0) porEtapa[slug as EtapaSlug] = v.sum / v.n;
  }
  const medias: MediasScore = {
    methodTotalAvg: methodCount > 0 ? methodSum / methodCount : null,
    scoreFinalAvg: finalCount > 0 ? finalSum / finalCount : null,
    porEtapa,
    gradeDistribution,
  };

  // ── sazonalidade ───────────────────────────────────────────────────────────
  const mesMap = new Map<string, number>();
  const diaMap = new Map<number, number>();
  for (const c of consultas) {
    const d = new Date(c.createdAt);
    if (Number.isNaN(d.getTime())) continue;
    const mesKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    mesMap.set(mesKey, (mesMap.get(mesKey) ?? 0) + 1);
    diaMap.set(d.getDay(), (diaMap.get(d.getDay()) ?? 0) + 1);
  }
  const porMes = Array.from(mesMap.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, count]) => {
      const [yyyy, mm] = key.split("-");
      const idx = parseInt(mm, 10) - 1;
      return { mes: `${MESES[idx] ?? mm}/${yyyy.slice(2)}`, count };
    });
  const porDiaSemana = DIAS_SEMANA.map((dia, i) => ({
    dia,
    count: diaMap.get(i) ?? 0,
  }));

  return {
    total,
    comAnaliseValida,
    intervaloDatas,
    personalidades,
    flags,
    etapaCriticaFreq,
    gargalos,
    campoSemantico,
    medias,
    sazonalidade: { porMes, porDiaSemana },
  };
}
